/* Search Sessions - v0.1.0 - 2016-01-04
 * Copyright (c) 2016 Contexity AG; Licensed
 * exported SearchSessions
 *
 # Search Sessions

 Support for search sessions as a JS "module".

 ## Getting Started

 General form:
 ```javascript
 var session = window.scsSiteSearch.SearchSessions([options]).getSession(query[,action]);
 ```
 where the parameters are as follows:
 - **options**: an optional configuration object, which supports the following parameters
 -	*timeout*: the numbers of milliseconds after which a search session expires
 - *min_shared_terms*: the minimum number of terms two subsequent queries must have in common in order to be considered to belong to the same search session
 -	*max_distance*: the maximum edit distance two subsequent queries must have in common in order to be considered to belong to the same search session
 - **query**: the query text entered by the user (**Note!** Not satinized internally!)
 - **action**: an optional forced session-update action with the following possible values:
 -	*"new"*: causes a new session to be created, reseting sequences if necessary
 -	*"next"*: forces an update of the session, even if the session continuation criteria are met
 -	*"same"*: forces the session to remain the same, even if the session continuation criteria are not met

 The default options if none are specified are as follows:
 ```javascript
 {
 timeout: 180000,
 min_shared_terms: 1,
 max_distance: 3,
 }
 ```

 The session objects returned have the following attributes:
 -  **created**: timestamp of the time at which the session was created
 -  **accessed**: timestamp of the time at which the session was last retrieved
 -  **ssid**: the session's ID
 -  **query**: the query text entered by the user (**Note!** Not sanitized internally!)

 */
(function(SCS, JSON, $) {
	'use strict';

	var search = SCS.search = SCS.search || {};
	var noop = function() {};

	var currentTS = Date.now || function() { return +new Date(); };

	// original Levenshtein distance code: Copyright (c) 2011 Andrei Mackenzie (https://gist.github.com/andrei-m/982927)
	// transposition ammendment by Contexity
	function editDistance(a, b) {
		if (a.length === 0) {
			return b.length;
		}
		if (b.length === 0) {
			return a.length;
		}

		var matrix = [];

		// increment along the first column of each row
		var i;
		for (i = 0; i <= b.length; i+=1) {
			matrix[i] = [i];
		}

		// increment each column in the first row
		var j;
		for (j = 0; j <= a.length; j+=1) {
			matrix[0][j] = j;
		}

		// Fill in the rest of the matrix
		for (i = 1; i <= b.length; i+=1) {
			for (j = 1; j <= a.length; j+=1) {
				if (b.charAt(i - 1) === a.charAt(j - 1)) {
					matrix[i][j] = matrix[i - 1][j - 1];
					continue;
				}
				matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1,   // substitution
					Math.min(matrix[i][j - 1] + 1,                  // insertion
						matrix[i - 1][j] + 1));                     // deletion
				if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) &&
					a.charAt(i - 2) === b.charAt(j - 1)) {
					matrix[i][j] = Math.min(matrix[i][j],
						matrix[i - 2][j - 2] + 1);                  // transposition
				}
			}
		}

		return matrix[b.length][a.length];
	}

	// compares the terms in two query strings and returns the number of terms that occur in both
	var TERM_SEPARATOR = /\s+/;
	function commonTerms(q1, q2) {

		if (!q1 || !q2) {
			return 0;
		}
		var t1 = q1.split(TERM_SEPARATOR), t2 = q2.split(TERM_SEPARATOR), d = {}, count = 0;
		for (var i = 0; i < t1.length; i+=1) {
			d[t1[i]] = true;
		}
		for (var j = 0; j < t2.length; j+=1) {
			if (d[t2[j]]) {
				count+=1;
			}
		}
		return count;
	}

	// RFC4122 v.4 compatible UUIDs
	// (http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript/2117523#2117523)
	function uuid() {
		// jshint bitwise: false
		var uuid =
			'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
				// var r = crypto.getRandomValues(new Uint8Array(1))[0] % 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
				var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
				return v.toString(16);
			});
		return uuid;
	}

	// Loads/sets session data
	var sessionStorage = null;
	try {
		sessionStorage = window.sessionStorage;
	} catch(e) {}
	var writer = (sessionStorage && function(name, value) {
		sessionStorage.setItem(name, value);
	}) || ($ && $.cookie && function(name, value) {
		window.$.cookie(name).set(value);
	}) || noop;
	var loader = (sessionStorage && function(name) {
		return sessionStorage.getItem(name);
	}) || ($ && $.cookie && function(name) {
		return $.cookie(name).get();
	}) || noop;

	// Encapsulates session data load/storage
	var defaultStorageHandler = {
		get: loader,
		set: writer
	};

	function defaultIDSequence(previous) {
		if (typeof(previous) === 'number') {
			return previous + 1;
		} else {
			return 1;
		}
	}
	
	var SESSION_SESSION_KEY = 'sc-search-session';

	function createSessionFactory(options, logger, idSequence, idGenerator, storageHandler) {
		logger = logger || window.debug;
		idSequence = idSequence || defaultIDSequence;
		idGenerator = idGenerator || uuid;
		storageHandler = storageHandler || defaultStorageHandler;

		// conditional console logging, may be replaced with pre-existing logging infrastructure
		function _log() {
			if(logger) {
				logger.log.apply(logger, arguments);
			}
		}

		// defaults...
		var _defaults = {
			timeout: 180000,
			min_shared_terms: 1,
			max_distance: 3,
			session_key: SESSION_SESSION_KEY
		};
		// ... extended by any options passed in
		var _options = search.util.extend({}, _defaults, options || {});

		// create a new search session with the given query text
		function _newSession(query, prevSession) {
			var now = currentTS();
			var sequence = idSequence(prevSession ? prevSession.sequence : null);
			var id = idGenerator();
			return {created: now, accessed: now, ssid: id, sequence: sequence, query: query};
		}

		// create a "successor" session to the one passed in as parameter
		function _nextSession(session, query) {
			if(!session) {
				_log('Invalid session passed to _nextSession:', session);
			}
			return _newSession(query, session);
		}

		// "touch" a session, updating its last accessed time, but leaving the ssid the same
		function _touchSession(session, query) {
			if(!session) {
				_log('Invalid session passed to _touchSession:', session);
			}
			session.accessed = currentTS();
			session.query = query || session.query || '';
			return session;
		}

		function queryDemandsNewSession(currentSession, query) {
			// we have the same session if:
			// 1. the queries have the minimum of common terms
			return (commonTerms(currentSession.query, query) < _options.min_shared_terms) &&
					// 2. have the maximum allowed edit distance
				(editDistance(currentSession.query, query) > _options.max_distance);
		}

		function timeDistanceDemandsNewSession(currentSession) {
			return (currentTS() - currentSession.accessed) >= _options.timeout;
		}

		function _needNextSession(currentSession, query) {
			return timeDistanceDemandsNewSession(currentSession) && queryDemandsNewSession(currentSession, query);
		}

		var currentSession = null;
		return function getSession(query, force) {
			if(currentSession === null) {
				// retrieve the current session if any
				currentSession = storageHandler.get(_options.session_key);
				if(currentSession) {
					currentSession = JSON.parse(currentSession);
				}
			}
			if (!currentSession) {
				currentSession = false;
				force = 'new';
			}

			if (!query) {
				// return previous session from cache
				return currentSession;
			}

			if(!force) {
				force = _needNextSession(currentSession, query) ? 'next' : 'current';
			}

			// force session behaviour
			if (force === 'new') {
				// always return a new session
				currentSession = _newSession(query);
			} else if (force === 'next') {
				// always return the next session
				currentSession = _nextSession(currentSession, query);
			} else if (force === 'current') {
				// always return the existing version, if there is one
				currentSession = _touchSession(currentSession, query);
			}

			// store the new session
			storageHandler.set(_options.session_key, JSON.stringify(currentSession));
			return currentSession;
		};

	}

	function defaultSessionFactory() {
		if(!defaultSessionFactory.instance) {
			defaultSessionFactory.instance = createSessionFactory();
		}
		return defaultSessionFactory.instance;
	}

	function addSession(sessionGetter) {
		sessionGetter = sessionGetter || defaultSessionFactory();
		// Generates a session for the query.
		// Can be added as soon as the query is known.
		function sessionAdder(config, callback) {
			var sessionOptions = config.sessionOptions || {};
			var force = sessionOptions.forceOnce || sessionOptions.force || null;
			config.forceSession = force;
			// Next request, even if derived, should not force the session again
			delete sessionOptions.forceOnce;
			var session = sessionGetter(config.query, force);
			config.session = session;
			config.ssid = session.ssid;
			config.sessionSequence = session.sequence;
			callback();
		}

		sessionAdder.sessionGetter = sessionGetter;

		return sessionAdder;
	}

	function setUp(processor, sessionAdder) {
		processor.usePre(sessionAdder||addSession(), -10000);
	}

	var plugins = search.plugins = search.plugins || {};
	var session = plugins.session = plugins.session || {};

	if(window.QUnit) {
		// Visible for testing
		var internals = session.internals = session.internals || {};
		internals.defaultStorageHandler = defaultStorageHandler;
		internals.defaultSessionFactory = defaultSessionFactory;
		internals.defaultIDSequence = defaultIDSequence;
		internals.uuid = uuid;
		internals.editDistance = editDistance;
		internals.commonTerms = commonTerms;
		internals.resetDefaultSession = function() {
			defaultSessionFactory.instance = null;
			defaultStorageHandler.set(SESSION_SESSION_KEY, '');
		};
	}

	session.createSessionFactory = createSessionFactory;
	session.addSession = addSession;
	session.setUp = setUp;

})(window.SCS = window.SCS || {}, window.JSON, 'Tc' in window ? window.Tc.$ : window.$);


//////////////////
// WEBPACK FOOTER
// ./clientlibs/js/etc/designs/sitesearch/clientlibs/logic/processing/js/plugins/session-handling.js
// module id = 301
// module chunks = 0